import type { AxiosError } from 'axios';
import i18n from 'i18next';

/**
 * What `errorMessageFromResponse` returns when the response carried no usable message (a network
 * failure, a gateway 502 with an empty body, `abort(400)` with no text): a translated "Something
 * went wrong". Callers that have a better context-specific fallback use `isApiErrorMessage` rather
 * than `?? fallback`, which never fires because the function always returns a string.
 */
export const unknownErrorMessage = (): string => i18n.t('DataLayer.unknownError');

const present = (value: unknown) =>
    value != null && !(typeof value === 'string' && value.trim() === '');

/**
 * The API's own message for a failed request. Never its `debug` field: that's the exception text
 * (SQL, file paths), which reaches Sentry through `describeApiFailure` but must not reach users.
 */
function errorMessageFromResponse(error: AxiosError<any> | null | undefined) {
    const data = error?.response?.data;
    const errorMessage = [data?.meta?.error, data?.message].find(present);

    return errorMessage == null ? unknownErrorMessage() : String(errorMessage);
}

/** True when the API gave a message of its own, i.e. not the `unknownErrorMessage` fallback. */
export const isApiErrorMessage = (message: string | null | undefined) =>
    present(message) && message !== unknownErrorMessage();

export default errorMessageFromResponse;
